import {
  getMessageStyle,
  useAppDispatch,
  useAppSelector,
  useGetEntitlementsQuery,
  useGetProvisionStatusQuery,
  useLazyGetUserOrgNodesQuery,
  useLazyGetVmStatusQuery,
  useProvisionInstanceMutation
} from '@monorepo/shared';
import {
  AdomVersionSelector,
  CountDown,
  ImageVersionSelector,
  ProvisionActions,
  ProvisionConfirm,
  RegionSelector,
  TimezoneSelector
} from '@monorepo/shared/components';
import { useEffect, useRef, useState } from 'react';
import { Col, Form, Grid, Loader, Message, Row, Stack, VStack } from 'rsuite';
import { server, HomeElms } from '../../config/const';
import { useTranslation } from 'react-i18next';
import {
  selectRegions,
  selectRejectedRegions
} from '@monorepo/shared/store/authSlice';
import serviceTermPdf from '/SD-CloudServices-FortiAnalyzer.pdf';
import { selectSystem } from '@monorepo/shared/store/accountSlice';

interface ProvisionIProps {
  rowData: Record<string, any>;
  existedTaskObj?: Record<string, any>;
}

const ProvisionService = ({ rowData, existedTaskObj }: ProvisionIProps) => {
  const { t } = useTranslation();
  const initInfo: Record<string, any> = {
    showIcon: true,
    type: 'info',
    text: <span>{t('common.provision.confirmInfo')}</span>
  };
  const systemData = useAppSelector(selectSystem);
  const rejectedRegions = useAppSelector(selectRejectedRegions);
  const regions = useAppSelector(selectRegions);
  const regionAlias = rowData?.region?.alias;
  const [message, setMessage] = useState<Record<string, any>>(initInfo);
  const [provisionInstance, getTaskIdResult] = useProvisionInstanceMutation();
  const [taskObj, setTaskObj] = useState<Record<string, any>>(
    existedTaskObj ? existedTaskObj : {}
  );
  const [getAccounts] = useLazyGetUserOrgNodesQuery();
  const [getVmStatus] = useLazyGetVmStatusQuery();
  const waitVmRef = useRef(false);
  const [imageVersion, setImageVersion] = useState<string>('');
  const [adomVersion, setAdomVersion] = useState<string>('');
  const [resetNum, setResetNum] = useState<number>(0);
  const [hasEntitlement, setHasEntitlement] = useState<boolean>(false);
  const [timezone, setTimezone] = useState<Record<string, any>>({
    label: '',
    rawValue: ''
  });
  const [region, setRegion] = useState<Record<string, any>>({
    rawValue: regionAlias,
    label: regionAlias
  });
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const {
    data: assets,
    isError: isAssetError,
    isSuccess: isAssetSuccess,
    isLoading: isAssetLoading
  } = useGetEntitlementsQuery(
    {
      server: server,
      ...rowData
    },
    {
      // skip: !rowData?.account_id || !rowData?.portals
      skip: !rowData?.account_id
    }
  );
  const {
    data: provisionStatus,
    error: provisionError,
    isSuccess: statusFilled
  } = useGetProvisionStatusQuery(
    {
      region: region?.rawValue,
      taskId: taskObj?.task_id,
      accountIds: [taskObj?.account_id || rowData?.account_id]
    },
    { skip: !taskObj?.task_id, pollingInterval: 5000 }
  );
  const getRequiredProps = () => {
    return {
      timezone: {
        value: timezone?.rawValue,
        errorMessage: `${t('common.provision.timezoneRequire')}`
      },
      region: {
        value: region?.rawValue,
        errorMessage: `${t('common.provision.regionRequire')}`
      }
    };
  };
  const refetch = async () => {
    await getAccounts({
      server: server,
      fcName: HomeElms?.fcName,
      refresh: true
    });
  };

  useEffect(() => {
    if (getTaskIdResult.error) {
      const data: Record<string, any> = getTaskIdResult.error;
      if (data?.data?.error_desc && setMessage) {
        setMessage({
          type: 'error',
          showIcon: true,
          text: `${data?.data?.error_desc} [Error_code: ${data?.data?.error_code}]`
        });
      }
    } else if (getTaskIdResult.data) {
      const data: Record<string, any> = getTaskIdResult.data;
      if (data?.task_id) {
        setTaskObj(data);
      } else if (data?.login_url) {
        console.warn('This instance already has url exists', data?.login_url);
        refetch();
      } else if (data?.error_desc && setMessage) {
        setMessage({
          type: 'error',
          showIcon: true,
          text: `${data?.error_desc} [Error_code: ${data?.error_code}]`
        });
      }
    }
  }, [getTaskIdResult]);
  useEffect(() => {
    const rejectedRegionNames = Object.keys(rejectedRegions);
    if (
      setMessage &&
      rejectedRegionNames.length &&
      rejectedRegionNames.includes(region?.rawValue)
    ) {
      setMessage({
        type: 'error',
        showIcon: true,
        text: t('common.provision.regionNotAvailable', {
          region: region?.label
        })
      });
    } else {
      setMessage && !provisionStatus && setMessage(initInfo);
    }
  }, [region, rejectedRegions, t]);
  useEffect(() => {
    if (provisionStatus) {
      if (provisionStatus.status === 'FAILURE' || provisionStatus.error_desc) {
        setTaskObj({});
        if (setMessage) {
          setMessage({
            type: 'error',
            showIcon: true,
            text: `${provisionStatus.error_desc} [Error_code: ${provisionStatus.error_code}]`
          });
        }
      } else if (
        provisionStatus?.stage ||
        provisionStatus.status === 'PENDING'
      ) {
        const stage = provisionStatus?.stage;
        const msg = provisionStatus.msg;
        if (stage === 'DONE') {
          const accountId = taskObj?.account_id || rowData?.account_id;
          const regionObj = regions?.[region?.rawValue];
          if (accountId && regionObj?.url && regionObj?.token) {
            if (!waitVmRef.current) {
              waitVmRef.current = true;
              const waitVmActive = async () => {
                const maxAttempts = 5;
                const delayMs = 6000;
                const account = {
                  account_id: accountId,
                  group_id: rowData?.group_id,
                  region: {
                    url: regionObj?.url,
                    token: regionObj?.token
                  }
                };
                for (let attempt = 1; attempt <= maxAttempts; attempt++) {
                  console.info(
                    `[${new Date().toLocaleTimeString('en-US', {
                      hour12: false
                    })}] ` +
                      `VM status attempt ${attempt} for account ${accountId}`
                  );
                  const res = await getVmStatus({ account });
                  if (res?.data?.is_active) {
                    console.info(`VM active on attempt ${attempt}`);
                    refetch();
                    setTaskObj({});
                    return;
                  }
                  if (attempt < maxAttempts) {
                    await new Promise((resolve) =>
                      setTimeout(resolve, delayMs)
                    );
                  }
                }
                // Still not active – proceed anyway to avoid a stuck UI.
                console.info(
                  `VM still not active after ${maxAttempts} attempts`
                );
                refetch();
                setTaskObj({});
              };
              waitVmActive().finally(() => {
                waitVmRef.current = false;
              });
            }
          } else {
            // call refetch after 10 seconds
            setTimeout(
              () => {
                refetch();
                setTaskObj({});
              },
              (systemData?.wait_time_before_vm_enter || 10) * 1000
            );
          }
        } else if (setMessage) {
          setMessage({
            type: 'success',
            showIcon: false,
            content: <CountDown stage={stage} msg={msg} refreshPage={refetch} />
          });
        }
      }
    }
    if (provisionError && setMessage) {
      const error: Record<string, any> = provisionError;
      setMessage({
        type: 'error',
        showIcon: true,
        text: `${t('common.provision.provisionFail')} ${
          error?.data?.detail
        } [Error_code: ${error?.status}]`
      });
      setTaskObj({});
    }
  }, [provisionStatus, provisionError, t, systemData]);
  useEffect(() => {
    if (isAssetSuccess) {
      const hasEntitlement = assets?.entitlements?.length > 0;
      setHasEntitlement(hasEntitlement);

      if (!hasEntitlement && setMessage) {
        setMessage({
          type: 'error',
          showIcon: true,
          text: t('common.provision.noEntitlement')
        });
      }
    } else if (isAssetError && setMessage) {
      setMessage({
        type: 'error',
        showIcon: true,
        text: t('common.provision.fetchEntitlementFailed')
      });
    }
  }, [assets, isAssetSuccess, isAssetError, t]);
  return (
    <Grid fluid className="instance-content">
      {isAssetLoading ? (
        <Loader center content={t('common.loading')} />
      ) : (
        <>
          <Row className="show-grid instance-content-row" gutter={0}>
            <Col span={24} className="instance-content-col">
              <div
                className="instance-content-container"
                style={{
                  marginRight: '10px'
                }}
              >
                <VStack className="instance-info" alignItems="stretch">
                  <VStack alignItems="stretch" spacing={10}>
                    <Stack justifyContent="space-between">
                      <b style={{ fontSize: '16px' }}>
                        {t('common.provision.provisionTitle')}
                      </b>
                    </Stack>
                    <Message
                      showIcon={message.showIcon}
                      type={message.type}
                      style={{
                        ...getMessageStyle(message.type),
                        marginBottom: '8px'
                      }}
                    >
                      <span
                        style={{ color: getMessageStyle(message.type).color }}
                      >
                        {message.content || message.text}
                      </span>
                    </Message>
                    {existedTaskObj?.task_id ||
                    taskObj?.task_id ||
                    !hasEntitlement ? (
                      <></>
                    ) : (
                      <ProvisionForm
                        region={region}
                        timezone={timezone}
                        setAdomVersion={setAdomVersion}
                        imageVersion={imageVersion}
                        setImageVersion={setImageVersion}
                        setTimezone={setTimezone}
                        setRegion={setRegion}
                        resetNum={resetNum}
                      />
                    )}
                  </VStack>
                </VStack>
              </div>
            </Col>
          </Row>
          <Row>
            <Col span={24} className="instance-content-controller">
              {hasEntitlement && (
                <ProvisionActions
                  requiredProps={getRequiredProps()}
                  resetNum={resetNum}
                  taskObj={taskObj}
                  getTaskIdResult={getTaskIdResult}
                  setDialogOpen={setDialogOpen}
                  setResetNum={setResetNum}
                />
              )}
              <ProvisionConfirm
                requiredProps={getRequiredProps()}
                dialogOpen={dialogOpen}
                setDialogOpen={setDialogOpen}
                rowData={rowData}
                timezone={timezone}
                region={region}
                imageVersion={imageVersion}
                adomVersion={adomVersion}
                provisionInstance={provisionInstance}
                pdf={serviceTermPdf}
                server={server}
              ></ProvisionConfirm>
            </Col>
          </Row>
        </>
      )}
    </Grid>
  );
};
interface IProps {
  imageVersion: string;
  setImageVersion: any;
  setAdomVersion: any;
  setTimezone: any;
  region: Record<string, any>;
  timezone: Record<string, any>;
  setRegion: any;
  resetNum: number;
}
const ProvisionForm = ({
  setRegion,
  region,
  imageVersion,
  setImageVersion,
  setAdomVersion,
  setTimezone,
  resetNum
}: IProps) => {
  const { t } = useTranslation();
  const [showImageVersion, setShowImageVersion] = useState<boolean>(true);
  const [showAdomVersion, setShowAdomVersion] = useState<boolean>(false);
  const setSelectedImageVersion = (selectedValue?: string) => {
    setImageVersion(selectedValue || '');
  };
  const setSelectedAdomVersion = (selectedValue?: string) => {
    setAdomVersion(selectedValue || '');
  };
  const setSelectedTimezone = (selectedItem?: Record<string, any>) => {
    setTimezone(selectedItem);
  };
  const setSelectedRegion = (selectedItem?: Record<string, any>) => {
    setShowImageVersion(selectedItem?.enableImageVersion);
    setShowAdomVersion(selectedItem?.enableAdomVersion);
    !selectedItem?.enableImageVersion && setImageVersion('');
    !selectedItem?.enableAdomVersion && setAdomVersion('');
    setRegion(selectedItem);
  };
  useEffect(() => {
    !showImageVersion && setImageVersion('');
    !showAdomVersion && setAdomVersion('');
  }, [showImageVersion, showAdomVersion]);
  return (
    <div>
      <Stack>
        <Form
          style={{
            marginLeft: '50px',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gridTemplateRows: 'auto',
            gap: '0px 60px'
          }}
        >
          <Form.Group
            controlId="region"
            className="provision-form-group"
            style={{
              marginBottom: '10px'
            }}
          >
            <Form.Label>{t('common.provision.region')}</Form.Label>
            <div className={'rs-form-control-wrapper'}>
              <RegionSelector
                onSelectHandle={setSelectedRegion}
                resetNum={resetNum}
                server={server}
                enableTimezone={true}
              />
            </div>
          </Form.Group>
          <Form.Group
            controlId="timezone"
            className="provision-form-group"
            style={{
              marginBottom: '10px',
              visibility: `${region ? 'visible' : 'hidden'}`
            }}
          >
            <Form.Label>{t('common.provision.timezone')}</Form.Label>
            <div className={'rs-form-control-wrapper'}>
              <TimezoneSelector
                region={region?.rawValue}
                resetNum={resetNum}
                onSelectHandle={setSelectedTimezone}
              />
            </div>
          </Form.Group>
          <Form.Group
            controlId="image-version"
            className="provision-form-group"
            style={{
              marginBottom: '10px',
              display: `${
                showImageVersion && region?.rawValue ? 'flex' : 'none'
              }`
            }}
          >
            <Form.Label>{t('common.provision.imageVersion')}</Form.Label>
            <div className={'rs-form-control-wrapper'}>
              <ImageVersionSelector
                region={region?.rawValue}
                resetNum={resetNum}
                onSelectHandle={setSelectedImageVersion}
              />
            </div>
          </Form.Group>
          <Form.Group
            controlId="adom-version"
            className="provision-form-group"
            style={{
              marginBottom: '10px',
              visibility: `${
                showAdomVersion && imageVersion ? 'visible' : 'hidden'
              }`
            }}
          >
            <Form.Label>{t('common.provision.adomVersion')}</Form.Label>
            <div className={'rs-form-control-wrapper'}>
              <AdomVersionSelector
                imageVersion={imageVersion}
                resetNum={resetNum}
                onSelectHandle={setSelectedAdomVersion}
              />
            </div>
          </Form.Group>
        </Form>
      </Stack>
    </div>
  );
};

export default ProvisionService;
